import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table, Tabs, Tab } from 'react-bootstrap';
import { FiUsers, FiCheckCircle, FiCalendar, FiXCircle } from 'react-icons/fi';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import PropTypes from 'prop-types';
import { fileAPI } from '../utils/api';
import './OMAttendanceDashboardPage.css';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateOnly = (value) => {
  if (!value) return '-';
  const text = String(value);
  return text.length >= 10 ? text.slice(0, 10) : text;
};

const formatHoursAgo = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const diffHours = Math.max(0, (Date.now() - date.getTime()) / (1000 * 60 * 60));
  return `${diffHours.toFixed(1)}h ago`;
};

const STAGE_STATUS_META = {
  om_morning_login: { icon: '🌅', label: 'Stage 1' },
  om_morning_approval: { icon: '✅', label: 'Stage 2' },
  om_evening_login: { icon: '🌆', label: 'Stage 3' },
  om_evening_approval: { icon: '🔒', label: 'Stage 4' },
  om_frt_activities: { icon: '🛠️', label: 'Stage 5' }
};

const STAGE_STATUS_ORDER = [
  'om_evening_approval',
  'om_evening_login',
  'om_frt_activities',
  'om_morning_approval',
  'om_morning_login'
];

const formatNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const pct = (v) => `${Number(v || 0).toFixed(1)}%`;

const renderAvailabilityValue = (value) => {
  const text = String(value || '-');
  const isNo = text.toUpperCase() === 'NO';
  return <span className={isNo ? 'text-danger fw-bold' : undefined}>{text}</span>;
};

const OmFrtActivitiesByTypeCard = ({ byActivity }) => (
  <Card className="shadow-sm h-100">
    <Card.Header className="bg-white border-bottom"><h6 className="mb-0">FRT Activities By Type</h6></Card.Header>
    <Card.Body>
      <div className="table-responsive">
        <Table striped bordered hover size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Activity</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(byActivity) && byActivity.length ? (
              byActivity.map((row, index) => (
                <tr key={row.name || index}>
                  <td>{row.name || '-'}</td>
                  <td>{row.count ?? 0}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} className="text-center text-muted">No activity data available.</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </Card.Body>
  </Card>
);

OmFrtActivitiesByTypeCard.propTypes = {
  byActivity: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    count: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }))
};

const OmFrtComplianceCard = ({ compliance }) => (
  <Card className="shadow-sm h-100">
    <Card.Header className="bg-white border-bottom"><h6 className="mb-0">FRT Activity Compliance</h6></Card.Header>
    <Card.Body>
      <p className="mb-2">
        <strong>Present FRTs with Activities:</strong> {`${compliance?.activeFRTCount || 0}/${compliance?.presentFRTCount || 0}`}
      </p>
      {!!(compliance?.inactivePresentFRTs || []).length && (
        <Alert variant="warning" className="mb-0">
          {`Inactive Present FRTs: ${(compliance?.inactivePresentFRTs || []).join(', ')}`}
        </Alert>
      )}
      {!((compliance?.inactivePresentFRTs || []).length) && <p className="text-success mb-0">No inactive present FRTs.</p>}
    </Card.Body>
  </Card>
);

OmFrtComplianceCard.propTypes = {
  compliance: PropTypes.shape({
    activeFRTCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    presentFRTCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    inactivePresentFRTs: PropTypes.arrayOf(PropTypes.string)
  })
};

const OmEquipmentSummaryCard = ({ equipmentSummary }) => (
  <Card className="shadow-sm h-100">
    <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Equipment Summary</h6></Card.Header>
    <Card.Body>
      <div className="table-responsive">
        <Table striped bordered hover size="sm" className="mb-0">
          <thead>
            <tr><th>Equipment</th><th>YES</th><th>NO</th></tr>
          </thead>
          <tbody>
            {Object.entries(equipmentSummary || {}).map(([name, value]) => (
              <tr key={name}>
                <td>{name}</td>
                <td>{value?.yes ?? 0}</td>
                <td>{value?.no ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </Card.Body>
  </Card>
);

OmEquipmentSummaryCard.propTypes = {
  equipmentSummary: PropTypes.arrayOf(PropTypes.object)
};

const OmEquipmentUnavailabilityCard = ({ chartData }) => (
  <Card className="shadow-sm h-100">
    <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Equipment Unavailability</h6></Card.Header>
    <Card.Body style={{ minHeight: 280 }}>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Bar dataKey="unavailable" fill="#dc2626" />
        </BarChart>
      </ResponsiveContainer>
    </Card.Body>
  </Card>
);

OmEquipmentUnavailabilityCard.propTypes = {
  chartData: PropTypes.arrayOf(PropTypes.object)
};

const OmFrtActivityRowsCard = ({ rows, activityStartDate, setActivityStartDate, activityEndDate, setActivityEndDate }) => (
  <Card className="shadow-sm">
    <Card.Header className="bg-white border-bottom"><h6 className="mb-0">FRT Activity Rows</h6></Card.Header>
    <Card.Body>
      <Row className="g-2 mb-3">
        <Col md={4}>
          <Form.Label className="mb-1">Start Date</Form.Label>
          <Form.Control type="date" value={activityStartDate} onChange={(e) => setActivityStartDate(e.target.value)} />
        </Col>
        <Col md={4}>
          <Form.Label className="mb-1">End Date</Form.Label>
          <Form.Control type="date" value={activityEndDate} onChange={(e) => setActivityEndDate(e.target.value)} />
        </Col>
      </Row>
      <div className="table-responsive">
        <Table striped bordered hover size="sm" className="mb-0">
          <thead>
            <tr>
              <th>FRT ID</th>
              <th>Activity</th>
              <th>District</th>
              <th>Taluka</th>
              <th>OTDR Distance (m)</th>
              <th>Trial Pits</th>
              <th>Joints</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).length ? (
              (rows || []).map((row, index) => (
                <tr key={row.id || `${row.frtId}-${row.activity}-${index}`}>
                  <td>{row.frtId || '-'}</td>
                  <td>{row.activity || '-'}</td>
                  <td>{row.districtName || '-'}</td>
                  <td>{row.talukaName || '-'}</td>
                  <td>{formatNumber(row.otdrDistanceInMeter)}</td>
                  <td>{formatNumber(row.numberOfTrialPit)}</td>
                  <td>{formatNumber(row.noOfJoints)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="text-center text-muted">No activity records in selected range.</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </Card.Body>
  </Card>
);

OmFrtActivityRowsCard.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  activityStartDate: PropTypes.string.isRequired,
  setActivityStartDate: PropTypes.func.isRequired,
  activityEndDate: PropTypes.string.isRequired,
  setActivityEndDate: PropTypes.func.isRequired
};

const OmFrtEquipmentAvailabilityCard = ({ morningApprovalRows }) => (
  <Card className="shadow-sm mt-3">
    <Card.Header className="bg-white border-bottom">
      <h6 className="mb-0">FRT Equipment Availability</h6>
    </Card.Header>
    <Card.Body>
      <div className="table-responsive">
        <Table striped bordered hover size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Field User Login ID</th>
              <th>Field User</th>
              <th>OTDR Available</th>
              <th>Laser Source Available</th>
              <th>Power Meter Available</th>
              <th>Splicing Machine Availability</th>
              <th>Splicer Available</th>
              <th>Assistant Splicer Available</th>
              <th>Driver Available</th>
            </tr>
          </thead>
          <tbody>
            {(morningApprovalRows || []).length ? (
              (morningApprovalRows || []).map((row, index) => (
                <tr key={`frt-equip-${row.fieldUserLoginId || index}`}>
                  <td>{row.fieldUserLoginId || '-'}</td>
                  <td>{row.fieldUser || '-'}</td>
                  <td>{renderAvailabilityValue(row.otdrAvailable)}</td>
                  <td>{renderAvailabilityValue(row.laserSourceAvailable)}</td>
                  <td>{renderAvailabilityValue(row.powerMeterAvailable)}</td>
                  <td>{renderAvailabilityValue(row.splicingMachineAvailability)}</td>
                  <td>{renderAvailabilityValue(row.splicerAvailable)}</td>
                  <td>{renderAvailabilityValue(row.assistantSplicerAvailable)}</td>
                  <td>{renderAvailabilityValue(row.driverAvailable)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="text-center text-muted">No equipment availability data.</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </Card.Body>
  </Card>
);

OmFrtEquipmentAvailabilityCard.propTypes = {
  morningApprovalRows: PropTypes.arrayOf(PropTypes.object)
};

const OmFrtPerformanceDayWiseTable = ({ performanceLoading, dayWise }) => (
  <div className="table-responsive">
    <Table striped bordered hover size="sm" className="mb-0">
      <thead>
        <tr>
          <th>Date</th>
          <th>Attendance</th>
          <th>Morning Approved</th>
          <th>Evening Login</th>
          <th>Evening Approved</th>
          <th>Start Reading</th>
          <th>End Reading</th>
        </tr>
      </thead>
      <tbody>
        {(() => {
          const rows = dayWise || [];
          const hasRows = rows.length > 0;

          if (performanceLoading) {
            return (
              <tr>
                <td colSpan={7} className="text-center text-muted">Loading performance details...</td>
              </tr>
            );
          }

          if (!hasRows) {
            return (
              <tr>
                <td colSpan={7} className="text-center text-muted">No day-wise records in selected range.</td>
              </tr>
            );
          }

          return rows.map((row) => (
            <tr key={row.day}>
              <td>{row.day}</td>
              <td>
                <Badge
                  bg={row.attendanceStatus === 'Present' ? 'success' : row.attendanceStatus === 'On Leave' ? 'warning' : 'secondary'}
                  text={row.attendanceStatus === 'On Leave' ? 'dark' : undefined}
                >
                  {row.attendanceStatus}
                </Badge>
              </td>
              <td>{row.morningApproved}</td>
              <td>{row.eveningLoggedIn}</td>
              <td>{row.eveningApproved}</td>
              <td>{row.startReading || '-'}</td>
              <td>{row.endReading || '-'}</td>
            </tr>
          ));
        })()}
      </tbody>
    </Table>
  </div>
);

OmFrtPerformanceDayWiseTable.propTypes = {
  performanceLoading: PropTypes.bool,
  dayWise: PropTypes.arrayOf(PropTypes.object)
};

const OmFrtPerformanceMonitoringCard = ({
  title = 'Individual FRT Performance Monitoring',
  selectedFrtId,
  setSelectedFrtId,
  frtIdOptions,
  performanceStartDate,
  setPerformanceStartDate,
  performanceEndDate,
  setPerformanceEndDate,
  performanceLoading,
  performanceData
}) => (
  <Card className="shadow-sm mt-3">
    <Card.Header className="bg-white border-bottom">
      <h6 className="mb-0">{title}</h6>
    </Card.Header>
    <Card.Body>
      <Row className="g-3 mb-3">
        <Col md={4}>
          <Form.Label className="mb-1">Select FRT ID</Form.Label>
          <Form.Select value={selectedFrtId} onChange={(e) => setSelectedFrtId(e.target.value)}>
            <option value="ALL">ALL</option>
            {!frtIdOptions.length ? <option value="">No FRT IDs</option> : null}
            {frtIdOptions.map((id) => <option key={id} value={id}>{id}</option>)}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Label className="mb-1">Start Date</Form.Label>
          <Form.Control type="date" value={performanceStartDate} onChange={(e) => setPerformanceStartDate(e.target.value)} />
        </Col>
        <Col md={4}>
          <Form.Label className="mb-1">End Date</Form.Label>
          <Form.Control type="date" value={performanceEndDate} onChange={(e) => setPerformanceEndDate(e.target.value)} />
        </Col>
      </Row>

      <h6 className="mb-3">Attendance and Approvals (Day-wise)</h6>

      <Row className="g-2 mb-3">
        <Col md={6} lg={3}>
          <Card className="om-stage-kpi-card h-100">
            <Card.Body>
              <p className="om-stage-kpi-title mb-1">Days with Attendance</p>
              <h5 className="om-stage-kpi-value mb-0">{performanceData?.kpis?.daysWithAttendance ?? 0}</h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3}>
          <Card className="om-stage-kpi-card h-100">
            <Card.Body>
              <p className="om-stage-kpi-title mb-1">Morning Approved Days</p>
              <h5 className="om-stage-kpi-value mb-0">{performanceData?.kpis?.daysMorningApproved ?? 0}</h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3}>
          <Card className="om-stage-kpi-card h-100">
            <Card.Body>
              <p className="om-stage-kpi-title mb-1">Evening Logged In Days</p>
              <h5 className="om-stage-kpi-value mb-0">{performanceData?.kpis?.daysEveningLoggedIn ?? 0}</h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3}>
          <Card className="om-stage-kpi-card h-100">
            <Card.Body>
              <p className="om-stage-kpi-title mb-1">Evening Approved Days</p>
              <h5 className="om-stage-kpi-value mb-0">{performanceData?.kpis?.daysEveningApproved ?? 0}</h5>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <OmFrtPerformanceDayWiseTable performanceLoading={performanceLoading} dayWise={performanceData?.dayWise || []} />
    </Card.Body>
  </Card>
);

OmFrtPerformanceMonitoringCard.propTypes = {
  title: PropTypes.string,
  selectedFrtId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setSelectedFrtId: PropTypes.func.isRequired,
  frtIdOptions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  performanceStartDate: PropTypes.string.isRequired,
  setPerformanceStartDate: PropTypes.func.isRequired,
  performanceEndDate: PropTypes.string.isRequired,
  setPerformanceEndDate: PropTypes.func.isRequired,
  performanceLoading: PropTypes.bool.isRequired,
  performanceData: PropTypes.shape({
    kpis: PropTypes.object,
    dayWise: PropTypes.arrayOf(PropTypes.object)
  })
};


const OmFrtExtrasSection = ({
  data,
  equipmentUnavailableChartData,
  activityStartDate,
  setActivityStartDate,
  activityEndDate,
  setActivityEndDate,
  selectedFrtId,
  setSelectedFrtId,
  frtIdOptions,
  performanceStartDate,
  setPerformanceStartDate,
  performanceEndDate,
  setPerformanceEndDate,
  performanceLoading,
  performanceData
}) => (
  <>
    <Row className="g-3 mb-3">
      <Col lg={6}>
        <OmFrtActivitiesByTypeCard byActivity={data?.frtActivities?.byActivity} />
      </Col>
      <Col lg={6}>
        <OmFrtComplianceCard compliance={data?.compliance} />
      </Col>
    </Row>

    <Row className="g-3 mb-3">
      <Col lg={6}>
        <OmEquipmentSummaryCard equipmentSummary={data?.equipmentSummary} />
      </Col>
      <Col lg={6}>
        <OmEquipmentUnavailabilityCard chartData={equipmentUnavailableChartData} />
      </Col>
    </Row>

    <OmFrtActivityRowsCard
      rows={data?.frtActivities?.rows || []}
      activityStartDate={activityStartDate}
      setActivityStartDate={setActivityStartDate}
      activityEndDate={activityEndDate}
      setActivityEndDate={setActivityEndDate}
    />

    <OmFrtEquipmentAvailabilityCard morningApprovalRows={data?.stageRows?.morningApproval || []} />

    <OmFrtPerformanceMonitoringCard
      selectedFrtId={selectedFrtId}
      setSelectedFrtId={setSelectedFrtId}
      frtIdOptions={frtIdOptions}
      performanceStartDate={performanceStartDate}
      setPerformanceStartDate={setPerformanceStartDate}
      performanceEndDate={performanceEndDate}
      setPerformanceEndDate={setPerformanceEndDate}
      performanceLoading={performanceLoading}
      performanceData={performanceData}
    />
  </>
);

OmFrtExtrasSection.propTypes = {
  data: PropTypes.object,
  equipmentUnavailableChartData: PropTypes.arrayOf(PropTypes.object),
  activityStartDate: PropTypes.string.isRequired,
  setActivityStartDate: PropTypes.func.isRequired,
  activityEndDate: PropTypes.string.isRequired,
  setActivityEndDate: PropTypes.func.isRequired,
  selectedFrtId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setSelectedFrtId: PropTypes.func.isRequired,
  frtIdOptions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  performanceStartDate: PropTypes.string.isRequired,
  setPerformanceStartDate: PropTypes.func.isRequired,
  performanceEndDate: PropTypes.string.isRequired,
  setPerformanceEndDate: PropTypes.func.isRequired,
  performanceLoading: PropTypes.bool.isRequired,
  performanceData: PropTypes.object
};

const OmDashboardLoading = () => (
  <div className="text-center py-5">
    <Spinner animation="border" variant="primary" />
    <p className="mt-3 mb-0">Loading O&M dashboard...</p>
  </div>
);

const OmSyncControlsRow = ({
  selectedDate,
  onChangeSelectedDate,
  roleFilter,
  onChangeRoleFilter,
  handleSyncStage,
  handleSyncAll,
  syncing,
  syncingStage
}) => (
  <Row className="g-2 mb-3 align-items-end flex-nowrap">
    <Col xs="auto">
      <Form.Control style={{ width: 190 }} type="date" value={selectedDate} onChange={(e) => onChangeSelectedDate(e.target.value)} />
    </Col>
    <Col xs="auto">
      <Form.Select style={{ width: 140 }} value={roleFilter} onChange={(e) => onChangeRoleFilter(e.target.value)}>
        <option value="FRT">FRT</option>
        <option value="TECHNICIAN">Technician</option>
      </Form.Select>
    </Col>
    <Col xs="auto">
      <Button size="sm" variant="outline-primary" onClick={() => handleSyncStage('morning-login', 'Stage 1: Morning Login')} disabled={syncing || !!syncingStage}>
        {syncingStage === 'morning-login' ? 'Syncing...' : 'Stage 1 Sync'}
      </Button>
    </Col>
    <Col xs="auto">
      <Button size="sm" variant="outline-primary" onClick={() => handleSyncStage('morning-approval', 'Stage 2: Morning Approval')} disabled={syncing || !!syncingStage}>
        {syncingStage === 'morning-approval' ? 'Syncing...' : 'Stage 2 Sync'}
      </Button>
    </Col>
    <Col xs="auto">
      <Button size="sm" variant="outline-primary" onClick={() => handleSyncStage('evening-login', 'Stage 3: Evening Login')} disabled={syncing || !!syncingStage}>
        {syncingStage === 'evening-login' ? 'Syncing...' : 'Stage 3 Sync'}
      </Button>
    </Col>
    <Col xs="auto">
      <Button size="sm" variant="outline-primary" onClick={() => handleSyncStage('evening-approval', 'Stage 4: Evening Approval')} disabled={syncing || !!syncingStage}>
        {syncingStage === 'evening-approval' ? 'Syncing...' : 'Stage 4 Sync'}
      </Button>
    </Col>
    <Col xs="auto">
      <Button size="sm" variant="outline-primary" onClick={() => handleSyncStage('frt-activities', 'Stage 5: FRT Activities')} disabled={syncing || !!syncingStage}>
        {syncingStage === 'frt-activities' ? 'Syncing...' : 'Stage 5 Sync'}
      </Button>
    </Col>
    <Col xs="auto" className="ms-auto">
      <Button onClick={handleSyncAll} disabled={syncing || !!syncingStage}>
        {(syncing || syncingStage === 'all') ? 'Syncing...' : 'Sync O&M Data'}
      </Button>
    </Col>
  </Row>
);

OmSyncControlsRow.propTypes = {
  selectedDate: PropTypes.string.isRequired,
  onChangeSelectedDate: PropTypes.func.isRequired,
  roleFilter: PropTypes.string.isRequired,
  onChangeRoleFilter: PropTypes.func.isRequired,
  handleSyncStage: PropTypes.func.isRequired,
  handleSyncAll: PropTypes.func.isRequired,
  syncing: PropTypes.bool.isRequired,
  syncingStage: PropTypes.string
};

const OmTotalsRow = ({ data }) => (
  <Row className="g-3 mb-3">
    <Col md={3}>
      <Card className="stat-card stat-primary h-100">
        <Card.Body className="d-flex align-items-center gap-2">
          <div className="stat-icon" style={{ width: 36, height: 36, fontSize: 16 }}><FiUsers /></div>
          <div>
            <p className="stat-title mb-1">Total Staff</p>
            <h4 className="mb-0 fw-bold">{data?.totals?.totalCount ?? 0}</h4>
          </div>
        </Card.Body>
      </Card>
    </Col>
    <Col md={3}>
      <Card className="stat-card stat-success h-100">
        <Card.Body className="d-flex align-items-center gap-2">
          <div className="stat-icon" style={{ width: 36, height: 36, fontSize: 16 }}><FiCheckCircle /></div>
          <div>
            <p className="stat-title mb-1">Present</p>
            <h4 className="mb-0 fw-bold">{data?.totals?.present ?? 0}</h4>
            <small className="text-muted">{pct(data?.attendanceSummary?.[0]?.percentage)}</small>
          </div>
        </Card.Body>
      </Card>
    </Col>
    <Col md={3}>
      <Card className="stat-card stat-warning h-100">
        <Card.Body className="d-flex align-items-center gap-2">
          <div className="stat-icon" style={{ width: 36, height: 36, fontSize: 16 }}><FiCalendar /></div>
          <div>
            <p className="stat-title mb-1">On Leave</p>
            <h4 className="mb-0 fw-bold">{data?.totals?.onLeave ?? 0}</h4>
            <small className="text-muted">{pct(data?.attendanceSummary?.[1]?.percentage)}</small>
          </div>
        </Card.Body>
      </Card>
    </Col>
    <Col md={3}>
      <Card className="stat-card stat-danger h-100">
        <Card.Body className="d-flex align-items-center gap-2">
          <div className="stat-icon" style={{ width: 36, height: 36, fontSize: 16 }}><FiXCircle /></div>
          <div>
            <p className="stat-title mb-1">Absent</p>
            <h4 className="mb-0 fw-bold">{data?.totals?.absent ?? 0}</h4>
            <small className="text-muted">{pct(data?.attendanceSummary?.[2]?.percentage)}</small>
          </div>
        </Card.Body>
      </Card>
    </Col>
  </Row>
);

OmTotalsRow.propTypes = {
  data: PropTypes.shape({
    totals: PropTypes.shape({
      totalCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      present: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      onLeave: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      absent: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    }),
    attendanceSummary: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      percentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    }))
  })
};

const OmAttendanceDetailsCard = ({ roleFilter, rows }) => (
  <Card className="shadow-sm mb-3">
    <Card.Header className="bg-white border-bottom">
      <h6 className="mb-0">{`${roleFilter} Attendance Details`}</h6>
    </Card.Header>
    <Card.Body>
      <div className="table-responsive">
        <Table striped bordered hover size="sm" className="mb-0">
          <thead>
            <tr>
              <th>ID</th>
              <th>Partner Name</th>
              <th>District</th>
              <th>Taluka</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).slice(0, 500).map((row, index) => (
              <tr key={row.id || index}>
                <td>{row.id || '-'}</td>
                <td>{row.partnerName || '-'}</td>
                <td>{row.district || '-'}</td>
                <td>{row.taluka || '-'}</td>
                <td>
                  <Badge
                    bg={row.attendanceStatus === 'Present' ? 'success' : row.attendanceStatus === 'On Leave' ? 'warning' : 'secondary'}
                    text={row.attendanceStatus === 'On Leave' ? 'dark' : undefined}
                  >
                    {row.attendanceStatus || '-'}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </Card.Body>
  </Card>
);

OmAttendanceDetailsCard.propTypes = {
  roleFilter: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object)
};

const OmStageTable = ({ meta }) => (
  <div className="table-responsive mt-3">
    <Table striped bordered hover size="sm" className="mb-0">
      <thead>
        <tr>
          {meta.columns.map((column) => <th key={column[0]}>{column[1]}</th>)}
        </tr>
      </thead>
      <tbody>
        {meta.rows.slice(0, 500).map((row, index) => (
          <tr key={row.fieldUserLoginId || index}>
            {meta.columns.map((column) => {
              const value = row?.[column[0]];
              const cellText = column[0] === 'uploadTime' ? formatDateTime(value) : (value || '-');
              return <td key={`${column[0]}-${index}`}>{cellText}</td>;
            })}
          </tr>
        ))}
      </tbody>
    </Table>
  </div>
);

OmStageTable.propTypes = {
  meta: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string
    })),
    rows: PropTypes.arrayOf(PropTypes.object)
  }).isRequired
};

const OmReadingAnalysisCard = ({ readingStats }) => (
  <Card className="shadow-sm mb-3">
    <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Start & End Reading Analysis</h6></Card.Header>
    <Card.Body>
      <Row>
        <Col md={3}><strong>Start Reading Count:</strong> {readingStats?.startReadingCount ?? 0}</Col>
        <Col md={3}><strong>Start Reading Avg:</strong> {formatNumber(readingStats?.startReadingAvg)}</Col>
        <Col md={3}><strong>End Reading Count:</strong> {readingStats?.endReadingCount ?? 0}</Col>
        <Col md={3}><strong>End Reading Avg:</strong> {formatNumber(readingStats?.endReadingAvg)}</Col>
      </Row>
    </Card.Body>
  </Card>
);

OmReadingAnalysisCard.propTypes = {
  readingStats: PropTypes.shape({
    startReadingCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    startReadingAvg: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    endReadingCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    endReadingAvg: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  })
};

const OmDatabaseStatusCard = ({ dbStatus }) => (
  <Card className="shadow-sm mt-3">
    <Card.Header className="bg-white border-bottom"><h6 className="mb-0">O&M Database Status</h6></Card.Header>
    <Card.Body>
      <p className="mb-2"><strong>Database Status</strong></p>
      <p className="mb-2"><strong>Record Counts:</strong></p>
      <p className="mb-1">{`• Morning Login: ${Number(dbStatus?.counts?.morningLoginCount || 0).toLocaleString('en-IN')}`}</p>
      <p className="mb-1">{`• Morning Approval: ${Number(dbStatus?.counts?.morningApprovalCount || 0).toLocaleString('en-IN')}`}</p>
      <p className="mb-1">{`• Evening Login: ${Number(dbStatus?.counts?.eveningLoginCount || 0).toLocaleString('en-IN')}`}</p>
      <p className="mb-1">{`• Evening Approval: ${Number(dbStatus?.counts?.eveningApprovalCount || 0).toLocaleString('en-IN')}`}</p>
      <p className="mb-3">{`• Frt Activities: ${Number(dbStatus?.counts?.frtActivitiesCount || 0).toLocaleString('en-IN')}`}</p>

      <p className="mb-3">
        {`Date Range: ${formatDateOnly(dbStatus?.dateRange?.minDate)} to ${formatDateOnly(dbStatus?.dateRange?.maxDate)}`}
      </p>

      <p className="mb-2"><strong>Last Sync:</strong></p>
      {(dbStatus?.lastSync || []).map((row, index) => (
        <p key={`${row.tableName}-${index}`} className="mb-1">
          {`• ${row.tableName || '-'}: ${formatHoursAgo(row.lastSyncAt)}`}
        </p>
      ))}

      <p className="mb-2 mt-3"><strong>Stage Status:</strong></p>
      {STAGE_STATUS_ORDER.map((tableName) => {
        const row = (dbStatus?.lastSync || []).find((item) => item.tableName === tableName);
        if (!row) return null;
        const meta = STAGE_STATUS_META[tableName] || { icon: '•', label: tableName };
        return (
          <p key={`stage-status-${tableName}`} className="mb-1">
            {`${meta.icon} ${meta.label}: ${formatHoursAgo(row.lastSyncAt)} (${Number(row.recordsSynced || 0).toLocaleString('en-IN')} records)`}
          </p>
        );
      })}
    </Card.Body>
  </Card>
);

OmDatabaseStatusCard.propTypes = {
  dbStatus: PropTypes.object
};

const OmInfoText = () => (
  <div className="mt-4">
    <p className="mb-2"><strong>FRT 4-Stage Attendance Monitoring Features:</strong></p>
    <p className="mb-1">Stage 1: Morning login with start reading capture and vehicle status</p>
    <p className="mb-1">Stage 2: Engineer approval of morning attendance with complete equipment verification</p>
    <p className="mb-1">Stage 3: Evening logout with end reading capture and work completion status</p>
    <p className="mb-1">Stage 4: Final engineer approval for complete attendance validation</p>
    <p className="mb-1">Stage 5: FRT activities tracking with metrics like OTDR distance and trial pits</p>
    <p className="mb-1">Attendance Summary: Real-time tracking of Present, On Leave, and Absent staff</p>
    <p className="mb-1">Reading Analysis: Start and end reading tracking with distance calculation</p>
    <p className="mb-3">Master Data Integration: Comprehensive staff tracking and performance metrics</p>

    <p className="mb-2"><strong>Key Performance Indicators:</strong></p>
    <p className="mb-1">Attendance Compliance: Monitoring of Present, On Leave, and Absent status</p>
    <p className="mb-1">Reading Compliance: Start and end reading capture verification</p>
    <p className="mb-1">Approval Efficiency: Morning and evening approval success rates</p>
    <p className="mb-1">Distance Analytics: Vehicle meter reading analysis for work verification</p>
    <p className="mb-3">Activity Metrics: OTDR distance, trial pits, cable lengths per FRT</p>

    <p className="mb-2"><strong>Data Sources:</strong></p>
    <p className="mb-1">API 1: Morning vehicle login data</p>
    <p className="mb-1">API 2: Engineer approval of morning attendance</p>
    <p className="mb-1">API 3: Evening vehicle logout data (same prikey)</p>
    <p className="mb-1">API 4: Final engineer approval for complete attendance cycle</p>
    <p className="mb-0">API 5: FRT activities data</p>
  </div>
);

const buildStageTabMeta = (data) => ({
  morningLogin: {
    title: 'Morning Login',
    rows: data?.stageRows?.morningLogin || [],
    columns: [
      ['fieldUserLoginId', 'Login ID'],
      ['fieldUser', 'Name'],
      ['workingStatus', 'Working Status'],
      ['startReading', 'Start Reading'],
      ['uploadTime', 'Upload Time']
    ]
  },
  morningApproval: {
    title: 'Morning Approval',
    rows: data?.stageRows?.morningApproval || [],
    columns: [
      ['fieldUserLoginId', 'Login ID'],
      ['fieldUser', 'Name'],
      ['approvedByEngineer', 'Approved'],
      ['engineerRemarks', 'Engineer Remarks'],
      ['uploadTime', 'Upload Time']
    ]
  },
  eveningLogin: {
    title: 'Evening Login',
    rows: data?.stageRows?.eveningLogin || [],
    columns: [
      ['fieldUserLoginId', 'Login ID'],
      ['fieldUser', 'Name'],
      ['endReadingAsPerVehicleMeter', 'End Reading'],
      ['uploadTime', 'Upload Time']
    ]
  },
  eveningApproval: {
    title: 'Evening Approval',
    rows: data?.stageRows?.eveningApproval || [],
    columns: [
      ['fieldUserLoginId', 'Login ID'],
      ['fieldUser', 'Name'],
      ['endEntryApprovedByEngineer', 'Approved'],
      ['finalEngineerRemarks', 'Final Engineer Remarks'],
      ['uploadTime', 'Upload Time']
    ]
  }
});

const OmStageDetailedDataCard = ({
  data,
  activeStageTab,
  setActiveStageTab,
  stageTabMeta,
  roleFilter,
  equipmentUnavailableChartData,
  activityStartDate,
  setActivityStartDate,
  activityEndDate,
  setActivityEndDate,
  selectedFrtId,
  setSelectedFrtId,
  frtIdOptions,
  performanceStartDate,
  setPerformanceStartDate,
  performanceEndDate,
  setPerformanceEndDate,
  performanceLoading,
  performanceData
}) => (
  <>
    <Card className="shadow-sm mb-3">
      <Card.Header className="bg-white border-bottom"><h6 className="mb-0">4-Stage Detailed Data</h6></Card.Header>
      <Card.Body>
        <Row className="g-2 mb-3">
          <Col md={6} lg={3}>
            <Card className="om-stage-kpi-card h-100"><Card.Body><p className="om-stage-kpi-title mb-1">Morning Login</p><h5 className="om-stage-kpi-value mb-0">{data?.stageFlow?.morningLoginCount ?? 0}</h5></Card.Body></Card>
          </Col>
          <Col md={6} lg={3}>
            <Card className="om-stage-kpi-card h-100"><Card.Body><p className="om-stage-kpi-title mb-1">Morning Approval</p><h5 className="om-stage-kpi-value mb-0">{data?.stageFlow?.morningApprovalCount ?? 0}</h5></Card.Body></Card>
          </Col>
          <Col md={6} lg={3}>
            <Card className="om-stage-kpi-card h-100"><Card.Body><p className="om-stage-kpi-title mb-1">Evening Login</p><h5 className="om-stage-kpi-value mb-0">{data?.stageFlow?.eveningLoginCount ?? 0}</h5></Card.Body></Card>
          </Col>
          <Col md={6} lg={3}>
            <Card className="om-stage-kpi-card h-100"><Card.Body><p className="om-stage-kpi-title mb-1">Evening Approval</p><h5 className="om-stage-kpi-value mb-0">{data?.stageFlow?.eveningApprovalCount ?? 0}</h5></Card.Body></Card>
          </Col>
        </Row>
        <Tabs activeKey={activeStageTab} onSelect={(k) => setActiveStageTab(k || 'morningLogin')} className="om-stage-tabs-horizontal">
          {Object.entries(stageTabMeta).map(([key, meta]) => (
            <Tab key={key} eventKey={key} title={meta.title}>
              <OmStageTable meta={meta} />
            </Tab>
          ))}
        </Tabs>
      </Card.Body>
    </Card>

    <OmReadingAnalysisCard readingStats={data?.readingStats} />

    {roleFilter === 'FRT' && (
      <OmFrtExtrasSection
        data={data}
        equipmentUnavailableChartData={equipmentUnavailableChartData}
        activityStartDate={activityStartDate}
        setActivityStartDate={setActivityStartDate}
        activityEndDate={activityEndDate}
        setActivityEndDate={setActivityEndDate}
        selectedFrtId={selectedFrtId}
        setSelectedFrtId={setSelectedFrtId}
        frtIdOptions={frtIdOptions}
        performanceStartDate={performanceStartDate}
        setPerformanceStartDate={setPerformanceStartDate}
        performanceEndDate={performanceEndDate}
        setPerformanceEndDate={setPerformanceEndDate}
        performanceLoading={performanceLoading}
        performanceData={performanceData}
      />
    )}

    {roleFilter === 'TECHNICIAN' && (
      <OmFrtPerformanceMonitoringCard
        title="Individual Technician Performance Monitoring"
        selectedFrtId={selectedFrtId}
        setSelectedFrtId={setSelectedFrtId}
        frtIdOptions={frtIdOptions}
        performanceStartDate={performanceStartDate}
        setPerformanceStartDate={setPerformanceStartDate}
        performanceEndDate={performanceEndDate}
        setPerformanceEndDate={setPerformanceEndDate}
        performanceLoading={performanceLoading}
        performanceData={performanceData}
      />
    )}
  </>
);

OmStageDetailedDataCard.propTypes = {
  data: PropTypes.object,
  activeStageTab: PropTypes.string.isRequired,
  setActiveStageTab: PropTypes.func.isRequired,
  stageTabMeta: PropTypes.object.isRequired
};

const OMAttendanceDashboardView = ({
  error,
  syncSuccessMessage,
  canSeePage,
  selectedDate,
  setSelectedDate,
  roleFilter,
  setRoleFilter,
  handleSyncStage,
  handleSyncAll,
  syncing,
  syncingStage,
  data,
  activeStageTab,
  setActiveStageTab,
  stageTabMeta,
  equipmentUnavailableChartData,
  activityStartDate,
  setActivityStartDate,
  activityEndDate,
  setActivityEndDate,
  selectedFrtId,
  setSelectedFrtId,
  frtIdOptions,
  performanceStartDate,
  setPerformanceStartDate,
  performanceEndDate,
  setPerformanceEndDate,
  performanceLoading,
  performanceData
}) => (
  <Container fluid className="om-dashboard-page py-4">
    {error && <Alert variant="danger">{error}</Alert>}
    {syncSuccessMessage && <Alert variant="success">{syncSuccessMessage}</Alert>}

    {!canSeePage ? (
      <Alert variant="warning">O&M dashboard is available only for O&M users and EXCO.</Alert>
    ) : (
      <>
        <OmSyncControlsRow
          selectedDate={selectedDate}
          onChangeSelectedDate={setSelectedDate}
          roleFilter={roleFilter}
          onChangeRoleFilter={setRoleFilter}
          handleSyncStage={handleSyncStage}
          handleSyncAll={handleSyncAll}
          syncing={syncing}
          syncingStage={syncingStage}
        />
        <OmTotalsRow data={data} />
        <OmAttendanceDetailsCard roleFilter={roleFilter} rows={data?.attendanceDetails || []} />
        <OmStageDetailedDataCard
          data={data}
          activeStageTab={activeStageTab}
          setActiveStageTab={setActiveStageTab}
          stageTabMeta={stageTabMeta}
          roleFilter={roleFilter}
          equipmentUnavailableChartData={equipmentUnavailableChartData}
          activityStartDate={activityStartDate}
          setActivityStartDate={setActivityStartDate}
          activityEndDate={activityEndDate}
          setActivityEndDate={setActivityEndDate}
          selectedFrtId={selectedFrtId}
          setSelectedFrtId={setSelectedFrtId}
          frtIdOptions={frtIdOptions}
          performanceStartDate={performanceStartDate}
          setPerformanceStartDate={setPerformanceStartDate}
          performanceEndDate={performanceEndDate}
          setPerformanceEndDate={setPerformanceEndDate}
          performanceLoading={performanceLoading}
          performanceData={performanceData}
        />
        <OmDatabaseStatusCard dbStatus={data?.dbStatus} />
        <OmInfoText />
      </>
    )}
  </Container>
);

OMAttendanceDashboardView.propTypes = {
  error: PropTypes.string,
  syncSuccessMessage: PropTypes.string,
  canSeePage: PropTypes.bool.isRequired,
  selectedDate: PropTypes.string.isRequired,
  setSelectedDate: PropTypes.func.isRequired,
  roleFilter: PropTypes.string.isRequired,
  setRoleFilter: PropTypes.func.isRequired,
  syncing: PropTypes.bool.isRequired,
  syncingStage: PropTypes.string,
  handleSyncStage: PropTypes.func.isRequired,
  handleSyncAll: PropTypes.func.isRequired,
  data: PropTypes.object,
  equipmentUnavailableChartData: PropTypes.arrayOf(PropTypes.object),
  activityStartDate: PropTypes.string.isRequired,
  setActivityStartDate: PropTypes.func.isRequired,
  activityEndDate: PropTypes.string.isRequired,
  setActivityEndDate: PropTypes.func.isRequired,
  selectedFrtId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setSelectedFrtId: PropTypes.func.isRequired,
  frtIdOptions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  performanceStartDate: PropTypes.string.isRequired,
  setPerformanceStartDate: PropTypes.func.isRequired,
  performanceEndDate: PropTypes.string.isRequired,
  setPerformanceEndDate: PropTypes.func.isRequired,
  performanceLoading: PropTypes.bool.isRequired,
  performanceData: PropTypes.object,
  activeStageTab: PropTypes.string.isRequired,
  setActiveStageTab: PropTypes.func.isRequired,
  stageTabMeta: PropTypes.object.isRequired
};

const OMAttendanceDashboardPage = ({ departmentId = null }) => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingStage, setSyncingStage] = useState('');
  const [syncSuccessMessage, setSyncSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [canSeePage, setCanSeePage] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [roleFilter, setRoleFilter] = useState('FRT');
  const [data, setData] = useState(null);
  const [activeStageTab, setActiveStageTab] = useState('morningLogin');
  const [selectedFrtId, setSelectedFrtId] = useState('ALL');
  const [performanceStartDate, setPerformanceStartDate] = useState(() => {
    return new Date().toISOString().slice(0, 10);
  });
  const [performanceEndDate, setPerformanceEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceData, setPerformanceData] = useState(null);
  const [activityStartDate, setActivityStartDate] = useState(() => {
    return new Date().toISOString().slice(0, 10);
  });
  const [activityEndDate, setActivityEndDate] = useState(new Date().toISOString().slice(0, 10));

  const equipmentUnavailableChartData = useMemo(() => (
    Object.entries(data?.equipmentSummary || {}).map(([name, value]) => ({
      name,
      unavailable: Number(value?.no || 0)
    }))
  ), [data]);

  const frtIdOptions = useMemo(() => {
    const prefix = roleFilter === 'TECHNICIAN' ? 'tech_' : 'frt_';
    const ids = (data?.attendanceDetails || [])
      .map((row) => String(row.id || '').trim())
      .filter((id) => id.toLowerCase().startsWith(prefix));
    return Array.from(new Set(ids)).sort((a, b) => a.localeCompare(b));
  }, [data, roleFilter]);

  useEffect(() => {
    if (!selectedFrtId && frtIdOptions.length) {
      setSelectedFrtId('ALL');
    }
  }, [frtIdOptions, selectedFrtId]);

  const fetchDashboard = useCallback(async (autoSync = false) => {
    if (!departmentId) {
      setLoading(false);
      setError('Department is required to load O&M dashboard.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await fileAPI.getOmAttendanceDashboard({
        departmentId,
        date: selectedDate,
        roleFilter,
        activityStartDate,
        activityEndDate,
        autoSync
      });
      setData(response?.data || null);
      setCanSeePage(true);
    } catch (err) {
      const message = typeof err === 'string'
        ? err
        : (err?.message || err?.error || 'Failed to load O&M dashboard.');
      const status = err?.status || err?.statusCode || 0;
      if (String(message).includes('403') || status === 403) {
        setCanSeePage(false);
      } else {
        setCanSeePage(true);
        setError(String(message));
      }
    } finally {
      setLoading(false);
    }
  }, [departmentId, selectedDate, roleFilter, activityStartDate, activityEndDate]);

  useEffect(() => {
    fetchDashboard(false);
  }, [fetchDashboard]);

  const fetchPerformanceData = useCallback(async () => {
    if (!departmentId || !selectedFrtId || selectedFrtId === 'ALL') {
      setPerformanceData(null);
      return;
    }

    try {
      setPerformanceLoading(true);
      const response = await fileAPI.getOmFRTPerformance({
        departmentId,
        frtId: selectedFrtId || 'ALL',
        roleFilter,
        startDate: performanceStartDate,
        endDate: performanceEndDate
      });
      setPerformanceData(response?.data || null);
    } catch (err) {
      // Keep dashboard usable even when optional performance endpoint fails.
      setPerformanceData(null);
      console.error('Failed to load FRT performance data:', err?.message || err);
    } finally {
      setPerformanceLoading(false);
    }
  }, [departmentId, selectedFrtId, performanceStartDate, performanceEndDate, roleFilter]);

  useEffect(() => {
    fetchPerformanceData();
  }, [roleFilter, fetchPerformanceData]);

  const handleSyncAll = async () => {
    if (!departmentId) return;
    try {
      setSyncing(true);
      setSyncingStage('all');
      setError('');
      setSyncSuccessMessage('');
      const response = await fileAPI.syncOmAttendanceDashboard({ departmentId, fullHistory: false, days: 30 });
      await fetchDashboard(false);

      if (response?.message) {
        setSyncSuccessMessage(response.message);
      } else {
        setSyncSuccessMessage('O&M sync started in the background. It will be ready in a minute.');
      }
    } catch (err) {
      const message = typeof err === 'string'
        ? err
        : (err?.message || err?.error || 'Failed to sync O&M dashboard.');
      setError(message);
    } finally {
      setSyncing(false);
      setSyncingStage('');
    }
  };

  const handleSyncStage = async (stageKey, stageLabel) => {
    if (!departmentId) return;
    try {
      setSyncingStage(stageKey);
      setError('');
      setSyncSuccessMessage('');
      const response = await fileAPI.syncOmAttendanceDashboard({ departmentId, fullHistory: false, days: 30, stage: stageKey });
      await fetchDashboard(false);

      if (response?.message) {
        setSyncSuccessMessage(response.message);
      } else {
        setSyncSuccessMessage(`${stageLabel} sync started in the background.`);
      }
    } catch (err) {
      const message = typeof err === 'string'
        ? err
        : (err?.message || err?.error || `Failed to sync ${stageLabel}.`);
      setError(message);
    } finally {
      setSyncingStage('');
    }
  };

  const stageTabMeta = useMemo(() => buildStageTabMeta(data), [data]);

  if (loading) return <OmDashboardLoading />;

  return (
    <OMAttendanceDashboardView
      error={error}
      syncSuccessMessage={syncSuccessMessage}
      canSeePage={canSeePage}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
      roleFilter={roleFilter}
      setRoleFilter={setRoleFilter}
      handleSyncStage={handleSyncStage}
      handleSyncAll={handleSyncAll}
      syncing={syncing}
      syncingStage={syncingStage}
      data={data}
      activeStageTab={activeStageTab}
      setActiveStageTab={setActiveStageTab}
      stageTabMeta={stageTabMeta}
      equipmentUnavailableChartData={equipmentUnavailableChartData}
      activityStartDate={activityStartDate}
      setActivityStartDate={setActivityStartDate}
      activityEndDate={activityEndDate}
      setActivityEndDate={setActivityEndDate}
      selectedFrtId={selectedFrtId}
      setSelectedFrtId={setSelectedFrtId}
      frtIdOptions={frtIdOptions}
      performanceStartDate={performanceStartDate}
      setPerformanceStartDate={setPerformanceStartDate}
      performanceEndDate={performanceEndDate}
      setPerformanceEndDate={setPerformanceEndDate}
      performanceLoading={performanceLoading}
      performanceData={performanceData}
    />
  );
};

OMAttendanceDashboardPage.propTypes = {
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default OMAttendanceDashboardPage;
